import {put, call, all} from 'redux-saga/effects';
import Campaign from "../../entities/campaign";
import * as Actions from "../actions/campaignActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type CampaignAction = {
    campaignId: string;
    campaign: Campaign;
    campaigns: Campaign[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneCampaign(action: CampaignAction) {
	const session = tabSession.captureGeneration();
	try {
		const campaign: Campaign = yield call(api.findOne, 'campaign', action.campaignId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_FETCH_ONE_SUCCESS, campaignId: action.campaignId, campaign, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CAMPAIGN_FETCH_ONE_FAILURE, campaignId: action.campaignId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {CampaignAction} action
 */
export function* saveCampaign(action: CampaignAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CAMPAIGN_SAVE saga', action.campaign?.id);

		if (action.campaign === undefined || action.campaign === null) {
			console.error('saveCampaign received a null');
			throw new Error('saveCampaign received a null');
		}

		if (!(action.campaign instanceof Campaign)) {
			action.campaign = new Campaign(action.campaign);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.campaign.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.campaign);
			const addResult = yield call(idb.add, 'campaign', action.campaign);
			if (addResult.isErr()) {
				console.error(`⮂ saveCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertCampaignSave(action.campaign, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CAMPAIGN_SAVE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CAMPAIGN_SAVE, campaign: action.campaign, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertCampaignSave(action.campaign, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CAMPAIGN_SAVE_FAILURE, campaign: action.campaign, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {CampaignAction} action
 * @param {Campaign[]} action.campaigns
 */
export function* saveManyCampaigns(action: CampaignAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CAMPAIGN_SAVE_MANY saga', action.campaigns.map(campaign => campaign.id).join(','));

        if (!Array.isArray(action.campaigns)) {
            console.error('saveManyCampaigns expected an array, received', action.campaigns);
            throw new Error('saveManyCampaigns expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveCampaign), under these queue keys
		const dispatchedAddedOns: number[] = action.campaigns.map(campaign => campaign?.addedOn);
		const revertAll = () => action.campaigns.map((campaign, i) =>
			put(Actions.revertCampaignSave(campaign, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Campaign[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.campaigns.length; i++) {
				const campaign = action.campaigns[i];
				campaign.persisted = false;
				ensureOperationId(campaign);
				toSave.push(campaign);
			}

			const addResult = yield call(idb.add, 'campaign', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyCampaigns: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CAMPAIGN_SAVE_MANY_FAILURE, campaigns: action.campaigns, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CAMPAIGN_SAVE_MANY, campaigns: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CAMPAIGN_SAVE_MANY_FAILURE, campaigns: action.campaigns, status: 500, err});

			console.error(`Error saving ${action.campaigns.length} campaigns to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {CampaignAction} action
 */
export function* removeCampaign(action: CampaignAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.campaign === undefined || action.campaign === null || !(action.campaign instanceof Campaign)) {
			console.error('removeCampaign expected type Campaign, received', action.campaign);
			reportQueued(false);
			throw new Error('removeCampaign expected type Campaign');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.campaign.status = 'D';
		action.campaign.addedOn = newHRTimestamp();
		action.campaign.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.campaign);
		const addResult = yield call(idb.add, 'campaign', action.campaign);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CAMPAIGN_REMOVE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_REMOVE, campaign: action.campaign});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CAMPAIGN_REMOVE_FAILURE, campaign: action.campaign, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Campaign.
	*
	* @param {CampaignAction} action
	*/
export function* restoreCampaign(action: CampaignAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreCampaign expected getCurrentUserId() to return a value');
        return;
    }

	if (action.campaign === undefined || action.campaign === null) {
        console.error('restoreCampaign expected a Campaign, received null');
        throw new Error('restoreCampaign expected a Campaign, received null');
    }
    else if (!(action.campaign instanceof Campaign)) action.campaign = new Campaign(action.campaign); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.campaign.status = 'U';
		action.campaign.addedOn = newHRTimestamp();
		action.campaign.addedBy = userId;
		action.campaign.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.campaign);
		const addResult = yield call(idb.add, 'campaign', action.campaign);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreCampaign: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CAMPAIGN_RESTORE_FAILURE, campaign: action.campaign, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CAMPAIGN_RESTORE, campaign: action.campaign});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CAMPAIGN_RESTORE_FAILURE, campaign: action.campaign, status: 500, err});

		console.error('Error restoring from database', err);
	}
}